import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isMockAuth } from "../auth/config";
import AddVideoForm from "../components/AddVideoForm";
import LibraryPeek from "../components/LibraryPeek";
import VideoCard from "../components/VideoCard";
import {
  isApiConfigured,
  pollUntilReady,
  searchVideos,
  transcribeVideo,
  YouTubeSearchResult,
} from "../lib/api-client";
import {
  availableCategories,
  availableTechniques,
  filterVideos,
  isFilterActive,
  LibraryFilters,
  NO_FILTERS,
  toggleTechnique,
} from "../lib/library-filter";
import {
  createApiLibraryStore,
  createLocalLibraryStore,
  UserVideo,
} from "../lib/library-store";

const API_STORES = isApiConfigured && !isMockAuth;

function formatDuration(durationSec: number | null): string {
  if (!durationSec || durationSec < 1) return "";
  const hours = Math.floor(durationSec / 3600);
  const minutes = Math.floor((durationSec % 3600) / 60);
  const seconds = durationSec % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function Library() {
  const { user, signOut, getIdToken } = useAuth();
  const store = useMemo(() => {
    if (!user) return null;
    return API_STORES
      ? createApiLibraryStore(getIdToken)
      : createLocalLibraryStore(user.sub);
  }, [user, getIdToken]);
  const [videos, setVideos] = useState<UserVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [bookmarkingIds, setBookmarkingIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [filters, setFilters] = useState<LibraryFilters>(NO_FILTERS);

  const categories = useMemo(() => availableCategories(videos), [videos]);
  const techniques = useMemo(() => availableTechniques(videos), [videos]);
  const visibleVideos = useMemo(
    () => filterVideos(videos, filters),
    [videos, filters],
  );
  const filterActive = isFilterActive(filters);

  const savedVideoIds = useMemo(
    () => new Set(videos.map((video) => video.youtubeVideoId)),
    [videos],
  );

  const refresh = useCallback(async () => {
    if (!store) return;
    setVideos(await store.list());
    setLoading(false);
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!API_STORES) return;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setSearchLoading(true);
        setSearchError(null);
        try {
          const token = await getIdToken();
          if (!token) throw new Error("Sign in again before searching.");
          const results = await searchVideos(q, token);
          if (!cancelled) setSearchResults(results);
        } catch (e) {
          if (!cancelled) {
            setSearchResults([]);
            setSearchError(e instanceof Error ? e.message : String(e));
          }
        } finally {
          if (!cancelled) setSearchLoading(false);
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [getIdToken, searchQuery]);

  const onAdd = useCallback(
    async (video: UserVideo) => {
      if (!store) return;
      await store.add(video);
      await refresh();
    },
    [store, refresh],
  );

  const onTranscribe = useCallback(
    async (video: UserVideo) => {
      if (!store) return;
      const token = await getIdToken();
      if (!token) throw new Error("Sign in again before transcribing.");

      await store.add(video);
      await refresh();

      try {
        const initial = await transcribeVideo(video.youtubeVideoId, token);
        if (initial.status !== "ready") {
          await pollUntilReady(video.youtubeVideoId, token);
        }
      } finally {
        await refresh();
      }
    },
    [getIdToken, refresh, store],
  );

  const onRemove = useCallback(
    async (id: string) => {
      if (!store) return;
      await store.remove(id);
      await refresh();
    },
    [store, refresh],
  );

  const onBookmarkSearchResult = useCallback(
    async (result: YouTubeSearchResult) => {
      if (!store) return;
      setBookmarkingIds((ids) => new Set(ids).add(result.youtubeVideoId));
      try {
        await store.add({
          youtubeVideoId: result.youtubeVideoId,
          title: result.title,
          channel: result.channel,
          thumbnailUrl: result.thumbnailUrl,
          status: "bookmarked",
          savedAt: Date.now(),
        });
        await refresh();
      } finally {
        setBookmarkingIds((ids) => {
          const next = new Set(ids);
          next.delete(result.youtubeVideoId);
          return next;
        });
      }
    },
    [refresh, store],
  );

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-800">
        <Link
          to="/library"
          aria-label="Fretboard home"
          className="text-xl font-bold text-orange-400 hover:text-orange-300 transition"
        >
          <span className="sm:hidden">Fretboard</span>
          <span className="hidden sm:inline">🎸 Fretboard</span>
        </Link>
        <nav className="flex items-center gap-4 sm:gap-6 text-sm">
          <LibraryPeek />
          <span aria-hidden className="hidden sm:inline text-gray-700">
            ·
          </span>
          <Link
            to="/profile"
            className="text-gray-400 hover:text-orange-300 transition font-display tracking-wide"
            title={user?.email ?? undefined}
          >
            Profile
          </Link>
          <button
            type="button"
            onClick={signOut}
            className="text-gray-500 hover:text-gray-200 transition"
          >
            Sign out
          </button>
        </nav>
      </header>
      <main className="p-6 max-w-5xl mx-auto space-y-6">
        <AddVideoForm
          onAdd={onAdd}
          onTranscribe={API_STORES ? onTranscribe : undefined}
          skipClientOEmbed={API_STORES}
        />

        {API_STORES && (
          <section className="border-y border-gray-800/80 -mx-6 px-6 py-5 sm:rounded-lg sm:border sm:mx-0">
            <label
              htmlFor="youtube-search"
              className="font-display text-[10px] tracking-[0.28em] uppercase text-gray-500 block mb-2"
            >
              Search YouTube
            </label>
            <input
              id="youtube-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="song title, technique, theory topic…"
              className="w-full px-3 py-2 bg-gray-950 border border-gray-800 rounded text-gray-100 placeholder-gray-600 focus:outline-none focus:border-orange-500/60 transition"
            />
            <p className="mt-1.5 font-display-italic text-xs text-gray-600">
              Biased toward lessons and tutorials.
            </p>
            {(searchLoading || searchError || searchResults.length > 0) && (
              <div className="mt-4 space-y-3">
                {searchLoading && (
                  <p className="text-sm text-gray-500">Searching...</p>
                )}
                {searchError && (
                  <p className="text-sm text-red-400">{searchError}</p>
                )}
                {!searchLoading &&
                  !searchError &&
                  searchQuery.trim().length >= 2 &&
                  searchResults.length === 0 && (
                    <p className="text-sm text-gray-500">
                      No matching videos found.
                    </p>
                  )}
                {searchResults.map((result) => {
                  const saved = savedVideoIds.has(result.youtubeVideoId);
                  const bookmarking = bookmarkingIds.has(result.youtubeVideoId);
                  const duration = formatDuration(result.durationSec);
                  const isPreviewing =
                    previewingId === result.youtubeVideoId;
                  const togglePreview = () =>
                    setPreviewingId((prev) =>
                      prev === result.youtubeVideoId
                        ? null
                        : result.youtubeVideoId,
                    );
                  return (
                    <div
                      key={result.youtubeVideoId}
                      className="border border-gray-800 bg-gray-950 rounded overflow-hidden"
                    >
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center p-3">
                        <button
                          type="button"
                          onClick={togglePreview}
                          aria-label={
                            isPreviewing
                              ? "Hide preview"
                              : "Preview this video"
                          }
                          className="relative w-full sm:w-40 aspect-video bg-gray-900 rounded overflow-hidden shrink-0 group cursor-pointer"
                        >
                          <img
                            src={result.thumbnailUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          {duration && (
                            <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/80 text-xs text-white">
                              {duration}
                            </span>
                          )}
                          <span
                            aria-hidden
                            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition text-white text-3xl"
                          >
                            ▶
                          </span>
                        </button>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium leading-snug line-clamp-2">
                            {result.title}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500 truncate">
                            {result.channel}
                          </p>
                        </div>
                        <div className="flex gap-2 sm:flex-col sm:items-stretch shrink-0">
                          <button
                            type="button"
                            onClick={togglePreview}
                            className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded transition"
                          >
                            {isPreviewing ? "✕ Hide" : "▶ Preview"}
                          </button>
                          <button
                            type="button"
                            disabled={saved || bookmarking}
                            onClick={() => void onBookmarkSearchResult(result)}
                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-sm rounded transition"
                          >
                            {saved
                              ? "Saved"
                              : bookmarking
                                ? "Saving..."
                                : "Bookmark"}
                          </button>
                        </div>
                      </div>
                      {isPreviewing && (
                        <div className="aspect-video bg-black border-t border-gray-800">
                          <iframe
                            src={`https://www.youtube.com/embed/${result.youtubeVideoId}?autoplay=1&mute=1&rel=0&playsinline=1`}
                            title={`Preview: ${result.title}`}
                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        <section>
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <h2 className="flex items-baseline gap-3">
              <span className="text-lg font-semibold text-gray-100">
                Your library
              </span>
              {videos.length > 0 && (
                <span className="font-display-italic text-sm text-gray-500 tabular-nums">
                  {filterActive
                    ? `${visibleVideos.length} of ${videos.length}`
                    : `${videos.length} video${videos.length === 1 ? "" : "s"}`}
                </span>
              )}
            </h2>
            {filterActive ? (
              <button
                type="button"
                onClick={() => setFilters(NO_FILTERS)}
                className="font-display-italic text-xs text-gray-500 hover:text-orange-300 transition"
              >
                clear filters
              </button>
            ) : null}
          </div>

          {(categories.length > 0 || techniques.length > 0) && (
            <div className="mb-4 space-y-2.5">
              {categories.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-[10px] tracking-[0.28em] uppercase text-gray-600 mr-1 shrink-0">
                    Category
                  </span>
                  <FilterChip
                    active={filters.category === "all"}
                    onClick={() =>
                      setFilters((f) => ({ ...f, category: "all" }))
                    }
                  >
                    All
                  </FilterChip>
                  {categories.map((c) => (
                    <FilterChip
                      key={c}
                      active={filters.category === c}
                      onClick={() =>
                        setFilters((f) => ({
                          ...f,
                          category: filters.category === c ? "all" : c,
                        }))
                      }
                    >
                      {c}
                    </FilterChip>
                  ))}
                </div>
              )}

              {techniques.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-[10px] tracking-[0.28em] uppercase text-gray-600 mr-1 shrink-0">
                    Technique
                  </span>
                  {techniques.map((t) => {
                    const active = filters.techniques.has(t);
                    return (
                      <FilterChip
                        key={t}
                        active={active}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            techniques: toggleTechnique(f.techniques, t),
                          }))
                        }
                      >
                        {t}
                      </FilterChip>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <p className="text-gray-500 text-sm">Loading...</p>
          ) : videos.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No videos yet. Paste a YouTube URL above to bookmark one.
            </p>
          ) : visibleVideos.length === 0 ? (
            <p className="font-display-italic text-gray-500 text-sm">
              No videos match the current filters.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleVideos.map((v) => (
                <VideoCard
                  key={v.youtubeVideoId}
                  video={v}
                  onRemove={(id) => void onRemove(id)}
                />
              ))}
            </div>
          )}
        </section>

        {!API_STORES && (
          <p className="text-xs text-gray-600 pt-4 border-t border-gray-900">
            Library is stored locally in your browser (mock-auth mode).
          </p>
        )}
      </main>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-[11px] tracking-wide transition ${
        active
          ? "border-orange-400/70 text-orange-200 bg-orange-500/10"
          : "border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200"
      }`}
    >
      {children}
    </button>
  );
}
